import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardOnboardingTaxSubject, useSetOnboardingTaxId } from '../../../../queries/pxtraCard';
import { Button } from '../../atomic/button';
import { onboardingErrorMessage } from './errorMessage';

/** Germany grants 90 days from account opening; the bar spans that window. */
const TAX_ID_PERIOD_DAYS = 90;

type Props = {
  companyId: string;
  subjects: CardOnboardingTaxSubject[];
};

const Subject: FC<{ companyId: string; subject: CardOnboardingTaxSubject }> = ({
  companyId,
  subject
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState('');
  const { mutate: save, isPending } = useSetOnboardingTaxId(companyId);
  const elapsed = Math.max(TAX_ID_PERIOD_DAYS - subject.days_left, 0);

  return (
    <div className="mt-3 max-w-[460px]">
      <p className="m-0 text-sm font-medium">
        {subject.name}{' '}
        <span className="font-normal text-grey-600">
          (
          {subject.kind === 'company'
            ? t('pxtraCard:taxIdSubjectCompany')
            : t('pxtraCard:taxIdSubjectUbo')}
          )
        </span>
      </p>

      <div className="mt-1 flex items-baseline justify-between text-xs text-grey-600">
        <span>
          {t('pxtraCard:taxIdDeadline', {
            date: new Date(subject.due_on).toLocaleDateString('de-DE')
          })}
        </span>
        <span className="font-medium">
          {t('pxtraCard:taxIdDaysLeft', { count: subject.days_left })}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-grey-100">
        <div
          className="h-full bg-warning-main"
          style={{ width: `${(elapsed / TAX_ID_PERIOD_DAYS) * 100}%` }}
        />
      </div>

      {!subject.editable && (
        <p className="m-0 mt-2 text-xs text-grey-600">{t('pxtraCard:taxIdUboHint')}</p>
      )}

      {subject.editable && (
        <div className="mt-2 flex gap-2">
          <input
            className="box-border min-w-0 flex-1 rounded-lg border border-solid border-grey-100 px-3 py-2 text-sm"
            placeholder={t('pxtraCard:taxIdPlaceholder')}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <Button
            size="medium"
            variant="secondary"
            disabled={!value.trim() || isPending}
            onClick={() =>
              save(
                { subjectId: subject.id, taxIdentificationNumber: value.trim() },
                {
                  onError: (error) =>
                    enqueueSnackbar(
                      onboardingErrorMessage(error) ?? t('pxtraCard:onboardingActionFailed'),
                      { variant: 'error' }
                    )
                }
              )
            }
          >
            {t('pxtraCard:taxIdSave')}
          </Button>
        </div>
      )}
    </div>
  );
};

export const TaxIdBlock: FC<Props> = ({ companyId, subjects }) => (
  <>
    {subjects.map((subject) => (
      <Subject key={subject.id} companyId={companyId} subject={subject} />
    ))}
  </>
);
